// Warns when two leases of the same unit run at the same time within the
// settled costs: the engine then shares each overlapping day between them,
// which is rarely what the broker meant (usually the earlier lease was never
// ended).

import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import React from "react";
import { unitLeaseOverlaps } from "../../../../occupancy.utils";
import {
  ContractRef,
  Invoice,
  SettlementDraftState,
  settlementEligible,
} from "../../../../types";
import { fmtDate, Warn } from "./shared";
import { AssignableProperty } from "./types";

interface IContractOverlapWarningProps {
  contracts: ContractRef[];
  invoices: Invoice[];
  draft: SettlementDraftState;
  assignable: AssignableProperty[];
  settledInvoiceIds?: Set<string>;
}

export const ContractOverlapWarning: React.FC<IContractOverlapWarningProps> = ({
  contracts,
  invoices,
  draft,
  assignable,
  settledInvoiceIds,
}) => {
  const periods = invoices
    .filter(
      invoice =>
        draft.selectedInvoiceIds[invoice.id] &&
        settlementEligible(invoice) &&
        !settledInvoiceIds?.has(invoice.id),
    )
    .map(invoice => ({ from: invoice.periodFrom, to: invoice.periodTo }));
  const overlaps = unitLeaseOverlaps({ leases: contracts, periods });
  if (overlaps.length === 0) return null;

  const tenantOf = (id: string) =>
    contracts.find(contract => contract.id === id)?.tenantName ?? "";
  const unitLabelOf = (unitId: string) => {
    const unit = assignable.find(p => String(p.id) === String(unitId));
    return unit?.address ?? unit?.code ?? "";
  };

  return (
    <div style={{ marginBottom: 16 }}>
      <Warn>
        {overlaps.map(overlap => (
          <div key={`${overlap.first.id}-${overlap.second.id}`}>
            {getLocalizedText("settlement.distribution.contracts_overlap", {
              property: unitLabelOf(overlap.unitId),
              first: tenantOf(overlap.first.id),
              second: tenantOf(overlap.second.id),
              from: fmtDate(overlap.from),
              to: fmtDate(overlap.to),
            })}
          </div>
        ))}
      </Warn>
    </div>
  );
};
